import { useCallback, useInsertionEffect, useRef } from 'react';

/**
 * A callback whose identity NEVER changes but which always runs the latest
 * closure passed in.
 *
 * For event handlers handed to memoised children (`React.memo`): an inline
 * closure in the parent is a new reference on every render, which re-renders
 * the child and defeats the memo. Wrapping it here keeps the child's props
 * referentially stable while the handler still sees the parent's current
 * state and props.
 *
 * The latest closure is committed in an insertion effect — the earliest
 * commit-phase hook — so a child's layout/passive effect (and of course any
 * user event) already observes it. Not for values read during render.
 */
export function useStableCallback<Args extends unknown[], R>(
  fn: (...args: Args) => R,
): (...args: Args) => R {
  const latest = useRef(fn);
  useInsertionEffect(() => {
    latest.current = fn;
  });
  return useCallback((...args: Args) => latest.current(...args), []);
}
